/**
 * Sidebar — v3 navigation rail.
 *
 * Renders the four nav groups (Organization, Monitoring, Patient
 * Care, Operations) using route metadata from `@app/router`. Active
 * state is driven by react-router; per-group expanded state lives
 * in localStorage. Collapse/expand is mirrored to the legacy
 * `appSettings.isSideMenuPinned` flag so the rest of the app
 * (info pane width, etc.) stays in sync.
 *
 * Visual spec: dashboard-mockup.html .sidebar / .nav / .nav-item.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, matchPath, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { ALL_ROUTES } from '@app/router';
import { checkPermission } from '@shared/lib/checkPermissions';
import { clearTableFilters } from '@shared/lib/tableFilters';
import { navigateToRoute, setAlertsPreFilter, TABLE_KEYS } from 'redux_store';
import { Tooltip } from '@shared/ui';
import useSidebarState from '../model/useSidebarState';

const NAV_STORAGE_KEY = 'qhs.appShell.navGroups';

/**
 * Resolve the live route path with the current account uuid baked in.
 * Routes in ALL_ROUTES use `:currentAccountUuid` as a placeholder;
 * NavLink needs a fully resolved URL.
 */
const buildHref = (route, accountUuid) =>
  route.path.replace(':currentAccountUuid', accountUuid || '');

// i18n keys (sidebar namespace) for the nav group headers and the
// alert-panel group names; both fall back to the raw English label.
const GROUP_LABEL_KEYS = {
  organization: 'groupOrganization',
  monitoring: 'groupMonitoring',
  'patient-care': 'groupPatientCare',
  operations: 'groupOperations',
};

const ALERT_GROUP_LABEL_KEYS = {
  'Imminent Risk': 'alertGroupImminentRisk',
  'Help Seeking Behavior': 'alertGroupHelpSeekingBehavior',
  'Risk Factors Identified': 'alertGroupRiskFactorsIdentified',
};

const NAV_GROUPS = () => [
  {
    id: 'organization',
    label: 'Organization',
    items: [
      ALL_ROUTES.account?.listDS,
      ALL_ROUTES.user?.globalManagement,
      ALL_ROUTES.allergiEndResources?.dashboard,
      // QHSLAB Documents nav entry is temporarily hidden for the
      // restyle release — the customer's Google Drive link is not
      // ready yet. Re-add ALL_ROUTES.qhslabDocuments?.dashboard here
      // once the URL lands in QhslabDocumentsPage (the route itself
      // stays registered, same approach as the hidden header bell).
      // Providers is DS-only and moved into the accounts-list row
      // menu (backend guidance 2026-07-23) — no nav entry.
    ],
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    items: [
      ALL_ROUTES.dashboard?.root,
      ALL_ROUTES.dashboard?.populationHealth,
      ALL_ROUTES.dashboard?.assessmentPerformance,
      ALL_ROUTES.dashboard?.claimsManager,
      // BHI Dashboard — gated by its dedicated BHI_DASHBOARD_VIEW bit
      // (route permission, 2026-08-21).
      ALL_ROUTES.bhiDashboard?.root,
      ALL_ROUTES.alert?.list,
    ],
  },
  {
    id: 'patient-care',
    label: 'Patient Care',
    items: [
      ALL_ROUTES.patient?.list,
      ALL_ROUTES.assessment?.list,
      ALL_ROUTES.resource?.list,
      ALL_ROUTES.order?.list,
      ALL_ROUTES.program?.list,
      ALL_ROUTES.journey?.list,
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      ALL_ROUTES.claim?.list,
      ALL_ROUTES.reportable?.list,
      ALL_ROUTES.claimReport?.list,
      ALL_ROUTES.botActivity?.list,
      // Non-production testing tool, gated by PERMISSIONS_MANAGE (bit
      // 67). On a production build the route object is absent (see
      // permissionsTesting.js), so this is `undefined` and
      // `isItemVisible` drops it — the page's lazy chunk is still built
      // into the production bundle, it is simply unreachable from here.
      // The real gate is the backend answering 404 for the underlying
      // API, backed by the PERMISSIONS_MANAGE bit.
      ALL_ROUTES.permissionsTesting?.page,
    ],
  },
];

const QHSLAB_LOGO = (
  <svg className="qhs-shell-logo" viewBox="0 0 89 63" xmlns="http://www.w3.org/2000/svg">
    <g fill="var(--qhs-brand)">
      <path d="M30.87,49.74A18.87,18.87,0,1,1,49.74,30.87c0,.34,0,.66-.05,1L52,34.14l9.2-9.24a30.85,30.85,0,1,0-19.71,35L31.33,49.72Z" />
      <polygon points="81.47 22.8 76.34 17.66 61.71 32.35 50.96 43.14 47.54 39.7 38.54 30.67 34.42 34.81 29.44 39.81 30.43 40.81 37.95 48.36 46.85 57.3 50.96 61.42 81.46 30.8 85.45 26.79 81.47 22.8" />
    </g>
  </svg>
);

const ChevronIcon = props => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * Panel-toggle icons (Material "left_panel_close" / "left_panel_open").
 * Semantically clearer than a plain chevron — they show a sidebar panel
 * and an arrow indicating what the click will do.
 */
const PanelCloseIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 -960 960 960" fill="currentColor" aria-hidden>
    <path d="M663-344v-272L527-480l136 136ZM180-120q-24.75 0-42.37-17.63Q120-155.25 120-180v-600q0-24.75 17.63-42.38Q155.25-840 180-840h600q24.75 0 42.38 17.62Q840-804.75 840-780v600q0 24.75-17.62 42.37Q804.75-120 780-120H180Zm147-60v-600H180v600h147Zm60 0h393v-600H387v600Zm-60 0H180h147Z" />
  </svg>
);

const PanelOpenIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 -960 960 960" fill="currentColor" aria-hidden>
    <path d="M527-616v272l136-136-136-136ZM180-120q-24.75 0-42.37-17.63Q120-155.25 120-180v-600q0-24.75 17.63-42.38Q155.25-840 180-840h600q24.75 0 42.38 17.62Q840-804.75 840-780v600q0 24.75-17.62 42.37Q804.75-120 780-120H180Zm147-60v-600H180v600h147Zm60 0h393v-600H387v600Zm-60 0H180h147Z" />
  </svg>
);

// =============== styled containers ===============

const Aside = styled('aside')({
  gridArea: 'sidebar',
  background: 'var(--qhs-surface)',
  borderRight: '1px solid var(--qhs-border)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  position: 'relative',
  transition: 'background-color 220ms ease, border-color 220ms ease',
});

/**
 * Three-phase collapse / expand choreography:
 *   0..FADE_MS              everything fades out (icons, labels,
 *                           group headers, logo text, separators,
 *                           footer meta) — driven by `$contentHidden`
 *   FADE_MS..FADE_MS+RESIZE rail width and internal geometry resize —
 *                           driven by `$collapsed` flipping in redux
 *   FADE_MS+RESIZE..+FADE   content fades back in at the new layout
 *
 * Every opacity transition is a plain `opacity 150 ms ease` — the
 * orchestration lives in `handleToggle`, not in CSS delays, so all
 * elements stay perfectly in lockstep regardless of whether we are
 * collapsing or expanding.
 */
const FADE_MS = 150;
const RESIZE_MS = 250;
const FADE_TRANSITION = `opacity ${FADE_MS}ms ease`;

const fadeProp = p => p !== '$collapsed' && p !== '$contentHidden';

const SidebarHeader = styled('div', { shouldForwardProp: fadeProp })(({ $collapsed }) => ({
  height: 'var(--qhs-topbar-h)',
  minHeight: 'var(--qhs-topbar-h)',
  display: 'flex',
  alignItems: 'center',
  // Logo position is pinned by padding alone — no justify-content
  // flip — so the mark sits at the exact same x in both states:
  // 24 + 32 = 56 in the 80-px collapsed rail (right edge at 56,
  // then 24 px right padding = 80); in the 240-px expanded rail
  // the same 24 offset matches the left margin of all nav items.
  justifyContent: 'flex-start',
  padding: '0 24px',
  borderBottom: '1px solid var(--qhs-border)',
  gap: 10,
  '& .qhs-shell-logo': {
    width: 32,
    height: 32,
    flexShrink: 0,
    // Logo stays visible throughout — it doesn't move, so nothing
    // to hide. Only the text beside it fades.
  },
}));

const LogoText = styled('span', { shouldForwardProp: fadeProp })(
  ({ $collapsed, $contentHidden }) => ({
    fontFamily: "'Lato', sans-serif",
    fontSize: 17,
    fontWeight: 900,
    color: 'var(--qhs-text-1)',
    letterSpacing: '-0.3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    opacity: $collapsed || $contentHidden ? 0 : 1,
    width: $collapsed ? 0 : 'auto',
    marginLeft: 0,
    pointerEvents: $collapsed || $contentHidden ? 'none' : 'auto',
    transition: FADE_TRANSITION,
  })
);

/**
 * FooterToggle — flat icon button that lives in the sidebar footer
 * alongside the build + env pill. Mirrors the VS Code / Notion
 * pattern: toggle sits at the bottom of the nav rail, always
 * visible, does not pop outside the sidebar edge.
 */
const FooterToggle = styled('button', {
  shouldForwardProp: p => p !== '$contentHidden',
})(({ $contentHidden }) => ({
  width: 32,
  height: 32,
  borderRadius: 8,
  background: 'transparent',
  border: 'none',
  color: 'var(--qhs-text-3)',
  cursor: 'pointer',
  padding: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: `background-color 150ms, color 150ms, ${FADE_TRANSITION}`,
  flexShrink: 0,
  opacity: $contentHidden ? 0 : 1,
  pointerEvents: $contentHidden ? 'none' : 'auto',
  '&:hover': {
    background: 'var(--qhs-surface-hover)',
    color: 'var(--qhs-brand)',
  },
}));

const Nav = styled('nav', { shouldForwardProp: fadeProp })(({ $collapsed }) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: $collapsed ? '4px 8px 8px' : '8px 0 16px',
  '&::-webkit-scrollbar': { width: 4 },
  '&::-webkit-scrollbar-thumb': {
    background: 'var(--qhs-border)',
    borderRadius: 2,
  },
}));

const Group = styled('div', {
  shouldForwardProp: p => p !== '$collapsed' && p !== '$first' && p !== '$contentHidden',
})(({ $collapsed, $first, $contentHidden }) => ({
  position: 'relative',
  // Expanded mode: items extend to the rail edges so the active row's
  // left border lands flush with the sidebar's inner edge. Collapsed
  // mode: 0 padding because the icon-buttons are individually rounded.
  padding: $collapsed ? '0' : '2px 0',
  marginTop: $collapsed && !$first ? 6 : 0,
  paddingTop: $collapsed && !$first ? 6 : 0,
  transition: `margin-top ${RESIZE_MS}ms ease, padding-top ${RESIZE_MS}ms ease, padding ${RESIZE_MS}ms ease`,
  // Separator stripe implemented as a pseudo-element so its
  // opacity can be animated cleanly (border-top-color transitions
  // proved flaky when used through a CSS variable). It fades in
  // lockstep with every other element via `$contentHidden`.
  ...(!$first && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 8,
      right: 8,
      height: 1,
      background: 'var(--qhs-border)',
      opacity: $contentHidden ? 0 : $collapsed ? 1 : 0,
      transition: FADE_TRANSITION,
      pointerEvents: 'none',
    },
  }),
}));

const GroupHeader = styled('button', { shouldForwardProp: fadeProp })(
  ({ $collapsed, $contentHidden }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    // 18 px horizontal padding aligns the group label with the nav
    // item text below, so each section reads as a left-aligned column
    // rather than two indents.
    padding: $collapsed ? '0 8px' : '8px 18px',
    cursor: $collapsed ? 'default' : 'pointer',
    borderRadius: 0,
    gap: 8,
    userSelect: 'none',
    background: 'transparent',
    border: 'none',
    color: 'var(--qhs-text-3)',
    overflow: 'hidden',
    // Max-height collapses the vertical space during the resize
    // phase; opacity fade is driven by the orchestrator so it runs
    // in lockstep with every other element.
    maxHeight: $collapsed ? 0 : 40,
    opacity: $collapsed || $contentHidden ? 0 : 1,
    pointerEvents: $collapsed || $contentHidden ? 'none' : 'auto',
    marginBottom: $collapsed ? 0 : 2,
    transition: `${FADE_TRANSITION}, max-height ${RESIZE_MS}ms ease, padding ${RESIZE_MS}ms ease, margin ${RESIZE_MS}ms ease`,
    '&:hover': $collapsed ? undefined : { background: 'var(--qhs-surface-hover)' },
  })
);

const GroupLabel = styled('span')({
  flex: 1,
  textAlign: 'left',
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--qhs-text-3)',
  letterSpacing: '1.2px',
  textTransform: 'uppercase',
});

const GroupChevron = styled(ChevronIcon, {
  shouldForwardProp: prop => prop !== '$collapsed',
})(({ $collapsed }) => ({
  color: 'var(--qhs-text-3)',
  transition: 'transform 200ms',
  width: 10,
  height: 10,
  transform: $collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
}));

const Subnav = styled('div', { shouldForwardProp: fadeProp })(({ $collapsed }) => ({
  paddingTop: $collapsed ? 0 : 2,
  paddingBottom: $collapsed ? 0 : 6,
}));

const NavItemLink = styled(NavLink, {
  shouldForwardProp: p => p !== '$collapsed' && p !== '$contentHidden',
})(({ $collapsed, $contentHidden }) => ({
  display: 'flex',
  alignItems: 'center',
  // Full-width so the button fills the rail and `justify-content`
  // can actually center the single remaining child (the icon) in
  // collapsed mode — a plain NavLink (<a>) would otherwise be
  // content-sized and the icon would hug the left edge.
  width: '100%',
  boxSizing: 'border-box',
  justifyContent: $collapsed ? 'center' : 'flex-start',
  padding: $collapsed ? '10px 0' : '8px 18px',
  gap: $collapsed ? 0 : 10,
  color: 'var(--qhs-text-2)',
  // Expanded: full-width row with an always-rendered transparent
  // left border that switches to brand colour when active. Collapsed:
  // rounded icon-button as before.
  borderRadius: $collapsed ? 8 : 0,
  borderLeft: $collapsed ? 'none' : '2px solid transparent',
  cursor: 'pointer',
  // Fading the whole link (not just the icon) is what keeps the
  // active-state background, the indicator bar, and the icon
  // perfectly in sync with every other element on the same beat.
  opacity: $contentHidden ? 0 : 1,
  pointerEvents: $contentHidden ? 'none' : 'auto',
  transition: `background-color 150ms, color 150ms, border-color 150ms, padding ${RESIZE_MS}ms ease, gap ${RESIZE_MS}ms ease, ${FADE_TRANSITION}`,
  position: 'relative',
  fontSize: 13,
  textDecoration: 'none',
  '&:hover': {
    background: 'var(--qhs-surface-hover)',
    color: 'var(--qhs-text-1)',
  },
  '&.is-active': {
    color: 'var(--qhs-brand)',
    fontWeight: 600,
    background: $collapsed
      ? 'linear-gradient(135deg, rgba(0, 168, 255, 0.08), rgba(46, 72, 211, 0.06))'
      : 'linear-gradient(90deg, rgba(46, 72, 211, 0.08), rgba(46, 72, 211, 0.02) 60%, transparent)',
    ...(!$collapsed && {
      borderLeftColor: 'var(--qhs-brand)',
    }),
  },
  // Collapsed-mode active marker — small pill bar at the very left
  // edge of the rail. Skipped in expanded mode because the row
  // already gets a 2 px left border from the rule above.
  ...($collapsed && {
    '&.is-active::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '50%',
      transform: 'translateY(-50%)',
      width: 3,
      height: 18,
      background: 'var(--qhs-brand)',
      borderRadius: '0 2px 2px 0',
    },
  }),
  '& svg': {
    width: 18,
    height: 18,
    flexShrink: 0,
    // Belt-and-suspenders centering: auto-margin centers the icon
    // horizontally regardless of whether the flex container was
    // measured correctly.
    ...($collapsed ? { margin: '0 auto' } : {}),
  },
  // Legacy SVG icons under _legacy/components/Shared/Icons/*Logo.jsx
  // default to `fill="#FFFFFF"` because they were drawn for the old
  // blue-gradient sidebar. On the v3 white surface they were invisible.
  // The rules below repaint every SVG <g> / <path> that carries a
  // non-"none" fill attribute with the item's currentColor, which in
  // turn inherits from text-2 / brand per `&.is-active` above.
  '& svg g[fill], & svg path[fill], & svg polygon[fill], & svg rect[fill], & svg circle[fill]': {
    fill: 'currentColor',
  },
  '& svg [fill="none"]': { fill: 'none' },
}));

const NavLabel = styled('span', { shouldForwardProp: fadeProp })(({ $collapsed }) => ({
  flex: $collapsed ? 'none' : 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  // The parent NavItemLink already fades the whole row via
  // `$contentHidden`, so the label only needs to track the
  // collapsed/expanded target opacity.
  opacity: $collapsed ? 0 : 1,
  width: $collapsed ? 0 : 'auto',
  minWidth: 0,
  marginLeft: 0,
  pointerEvents: $collapsed ? 'none' : 'auto',
  transition: FADE_TRANSITION,
}));

/**
 * Alerts badge — severity-only dot, no numeric payload. Colour
 * encodes the signal (red = at least one critical, blue = at
 * least one non-critical, nothing = clear). Exact counts live
 * inside the dropdown that opens from the same nav item, so the
 * rail itself stays quiet and minimalist.
 */
const IconBadgeWrap = styled('span')({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const dotBackground = $critical => ($critical ? 'var(--qhs-st-critical-tx)' : 'var(--qhs-primary)');

const NavBadgeInline = styled('span', {
  shouldForwardProp: p => p !== '$critical',
})(({ $critical }) => ({
  width: 8,
  height: 8,
  borderRadius: 999,
  background: dotBackground($critical),
  flexShrink: 0,
  pointerEvents: 'none',
}));

const NavBadgeFloat = styled('span', {
  shouldForwardProp: p => p !== '$critical',
})(({ $critical }) => ({
  position: 'absolute',
  top: -2,
  right: -2,
  width: 8,
  height: 8,
  borderRadius: 999,
  background: dotBackground($critical),
  border: '2px solid var(--qhs-surface)',
  boxSizing: 'content-box',
  pointerEvents: 'none',
}));

/* =============================================================
   Alerts dropdown — reuses the chrome of TopBar's DropdownMenu
   but is anchored off a nav item, so it uses fixed positioning
   against viewport coordinates captured at open time.
   ============================================================= */

const AlertsPanel = styled('div')({
  position: 'fixed',
  zIndex: 200,
  minWidth: 320,
  maxHeight: 420,
  overflowY: 'auto',
  background: 'var(--qhs-surface)',
  border: '1px solid var(--qhs-border)',
  borderRadius: 12,
  boxShadow: 'var(--qhs-sh-3)',
  padding: 6,
});

const AlertsPanelLabel = styled('div')({
  padding: '6px 10px',
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: 'var(--qhs-text-3)',
});

const AlertsPanelItem = styled('button')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '8px 10px',
  background: 'transparent',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: 'var(--qhs-text-1)',
  fontSize: 13,
  '&:hover': { background: 'var(--qhs-surface-hover)' },
});

const AlertsPanelDivider = styled('div')({
  height: 1,
  background: 'var(--qhs-border)',
  margin: '6px 4px',
});

const AlertsPanelEmpty = styled('div')({
  padding: '16px 12px',
  fontSize: 13,
  color: 'var(--qhs-text-3)',
  textAlign: 'center',
});

const CriticalTag = styled('span')({
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: '0.5px',
  color: '#fff',
  background: 'var(--qhs-st-critical-tx)',
  padding: '2px 6px',
  borderRadius: 4,
  textTransform: 'uppercase',
});

const SidebarFooter = styled('div', { shouldForwardProp: fadeProp })(({ $collapsed }) => ({
  padding: $collapsed ? '8px 6px' : '8px 10px',
  borderTop: '1px solid var(--qhs-border)',
  fontSize: 11,
  color: 'var(--qhs-text-3)',
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  justifyContent: $collapsed ? 'center' : 'space-between',
  transition: 'padding 220ms ease',
}));

const FooterMeta = styled('div', { shouldForwardProp: fadeProp })(
  ({ $collapsed, $contentHidden }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
    overflow: 'hidden',
    opacity: $collapsed || $contentHidden ? 0 : 1,
    width: $collapsed ? 0 : 'auto',
    pointerEvents: $collapsed || $contentHidden ? 'none' : 'auto',
    transition: FADE_TRANSITION,
  })
);

const EnvPill = styled('span')({
  background: 'var(--qhs-st-high-bg)',
  color: 'var(--qhs-st-high-tx)',
  fontSize: 9,
  fontWeight: 700,
  padding: '2px 6px',
  borderRadius: 4,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
});

// All collapsed-vs-expanded styling now lives on the individual
// styled components via `$collapsed` props (prop-based styles have
// the same specificity as other base styles — no sx-override
// battles). The sx-based `collapsedSx` was removed.

// =============== component ===============

const loadGroupState = () => {
  try {
    const saved = localStorage.getItem(NAV_STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (err) {
    /* corrupted state — fall through to defaults */
  }
  return { organization: true, monitoring: true, 'patient-care': true, operations: true };
};

const persistGroupState = state => {
  try {
    localStorage.setItem(NAV_STORAGE_KEY, JSON.stringify(state));
  } catch (err) {
    /* quota / private mode — non-fatal */
  }
};

const Sidebar = () => {
  const { collapsed, toggle } = useSidebarState();
  const location = useLocation();

  const dispatch = useDispatch();
  const accountUuid = useSelector(state => state.user?.metadata?.currentAccount?.uuid);
  const userUuid = useSelector(state => state.user?.user?.uuid);
  const accountPermissions = useSelector(state => state.user?.metadata?.currentAccountPermissions);
  const isDistributionSponsor = useSelector(state => state.user?.isDistributionSponsor);
  const disabledFeatures = useSelector(state => state.appSettings.disabledFeatures);
  // state.i18n holds the active-language catalog directly (no `.en`
  // wrapper) — reading through `.en` silently dropped every translation
  // here and left the alerts panel English-only.
  const i18nCatalog = useSelector(s => s.i18n) || {};
  const titles = i18nCatalog.titles || {};
  const sidebarT = i18nCatalog.sidebar || {};
  // Alerts: newAlertList carries per-group { type, count }; alertTags
  // maps group key → filter payload; totalAlertsCount is the sum.
  // Badge rule is severity-only (no count): red if any critical, blue
  // if any non-critical, hidden otherwise. Exact counts surface inside
  // the dropdown that opens from the nav item itself.
  const { newAlertList, totalAlertsCount, alertTags } = useSelector(s => s.alerts) || {};
  const alertHasCritical = useMemo(
    () =>
      !!newAlertList &&
      Object.keys(newAlertList).some(
        k => newAlertList[k].count > 0 && newAlertList[k].type === 'critical'
      ),
    [newAlertList]
  );
  const alertHasAny = totalAlertsCount > 0;
  const alertsPath = ALL_ROUTES.alert?.list?.path;

  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertsAnchor, setAlertsAnchor] = useState(null);
  const alertsTriggerRef = useRef(null);
  const alertsPanelRef = useRef(null);

  const openAlertsAt = useCallback(target => {
    if (!target) return;
    const rect = target.getBoundingClientRect();
    setAlertsAnchor({ top: rect.top, left: rect.right + 8 });
    setAlertsOpen(true);
  }, []);

  const handleAlertsClick = useCallback(
    e => {
      // Override NavLink navigation — click opens the dropdown. Use
      // currentTarget so we always get the link element, even when
      // the click lands on a child icon or label.
      e.preventDefault();
      if (alertsOpen) {
        setAlertsOpen(false);
      } else {
        openAlertsAt(e.currentTarget);
      }
    },
    [alertsOpen, openAlertsAt]
  );

  const navigateToAlerts = useCallback(
    filters => {
      // Wipe the remembered filter for the alerts table BEFORE
      // dispatching the pre-filter. Otherwise `useFetchList`
      // (which reads the table-filter store ahead of the
      // caller's `baseFilter` on mount) revives whatever the user
      // last filtered on that page — the sidebar's fresh
      // selection ends up ignored on repeat visits.
      clearTableFilters(userUuid, accountUuid, [TABLE_KEYS.alertsTable, TABLE_KEYS.alertsDSTable]);
      dispatch(setAlertsPreFilter(filters));
      dispatch(
        navigateToRoute({
          route: {
            path: ALL_ROUTES.alert.list.path,
            title: titles.alerts,
            clearBreadcrumbs: true,
            params: { currentAccountUuid: accountUuid },
          },
        })
      );
      setAlertsOpen(false);
    },
    [dispatch, titles.alerts, userUuid, accountUuid]
  );

  // Click outside both the trigger and the panel closes the dropdown.
  useEffect(() => {
    if (!alertsOpen) return undefined;
    const handle = e => {
      if (
        !alertsTriggerRef.current?.contains(e.target) &&
        !alertsPanelRef.current?.contains(e.target)
      ) {
        setAlertsOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [alertsOpen]);

  const [expanded, setExpanded] = useState(loadGroupState);
  // Single orchestration flag for the fade-out / resize / fade-in
  // cascade — every element that should animate reads this one
  // boolean so the three phases stay perfectly synchronized.
  //
  //   t = 0              click → setContentHidden(true)
  //                      → icons, labels, group headers, logo text,
  //                        separators and footer meta all begin
  //                        fading out on the same `opacity 150ms`
  //                        transition
  //   t = FADE_MS        toggle() → redux flips `collapsed`
  //                      → rail width + geometry start resizing
  //                        (content is fully invisible at this point,
  //                        so no jumping is visible to the user)
  //   t = FADE_MS        + RESIZE_MS: setContentHidden(false)
  //                      → everything fades back in at the new layout
  const [contentHidden, setContentHidden] = useState(false);

  const handleToggle = useCallback(() => {
    setContentHidden(true);
    window.setTimeout(() => toggle(), FADE_MS);
    window.setTimeout(() => setContentHidden(false), FADE_MS + RESIZE_MS);
  }, [toggle]);

  useEffect(() => {
    persistGroupState(expanded);
  }, [expanded]);

  // Route change closes the dropdown. Covers the happy path — user
  // clicks "See all alerts" or a group entry — and the edge case
  // where navigation comes from elsewhere while the panel is open.
  useEffect(() => {
    setAlertsOpen(false);
  }, [location.pathname]);

  const toggleGroup = useCallback(id => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const isItemVisible = useCallback(
    item => {
      if (!item) return false;
      const perm = item.additional?.permission;
      if (perm && !checkPermission(accountPermissions, perm)) return false;
      const flag = item.additional?.disabledId;
      if (flag && disabledFeatures?.includes(flag)) return false;
      if (item.additional?.hideForDistributionSponsor && isDistributionSponsor) return false;
      return true;
    },
    [accountPermissions, disabledFeatures, isDistributionSponsor]
  );

  const groups = useMemo(
    () =>
      NAV_GROUPS()
        .map(group => ({
          ...group,
          visibleItems: group.items.filter(isItemVisible),
        }))
        .filter(group => group.visibleItems.length > 0),
    [isItemVisible]
  );

  const isItemActive = item => Boolean(matchPath(item.path, location.pathname));

  return (
    <Aside>
      <SidebarHeader $collapsed={collapsed}>
        {QHSLAB_LOGO}
        <LogoText $collapsed={collapsed} $contentHidden={contentHidden}>
          QHSLAB
        </LogoText>
      </SidebarHeader>

      <Nav $collapsed={collapsed}>
        {groups.map((group, idx) => {
          const isOpen = expanded[group.id] !== false;
          // In collapsed mode we skip the group header and always
          // render the items so the icon rail stays clickable.
          const showItems = collapsed || isOpen;
          return (
            <Group
              key={group.id}
              $collapsed={collapsed}
              $first={idx === 0}
              $contentHidden={contentHidden}
            >
              {/* GroupHeader stays in the DOM always so it can fade
                  in / out smoothly alongside labels and separators —
                  conditional removal would pop the chrome. */}
              <GroupHeader
                type="button"
                onClick={() => toggleGroup(group.id)}
                $collapsed={collapsed}
                $contentHidden={contentHidden}
                aria-hidden={collapsed}
                tabIndex={collapsed ? -1 : 0}
              >
                <GroupLabel>{sidebarT[GROUP_LABEL_KEYS[group.id]] || group.label}</GroupLabel>
                <GroupChevron $collapsed={!isOpen} />
              </GroupHeader>

              {showItems && (
                <Subnav $collapsed={collapsed}>
                  {group.visibleItems.map(item => {
                    const Icon = item.additional?.icon;
                    const title = item.additional?.title || item.additional?.name || item.path;
                    const isAlertItem = !!alertsPath && item.path === alertsPath;
                    const showBadge = isAlertItem && (alertHasCritical || alertHasAny);
                    const iconNode = Icon ? (
                      <Icon fill="currentColor" />
                    ) : (
                      <span style={{ width: 18 }} />
                    );
                    const navItem = (
                      <NavItemLink
                        key={item.path}
                        to={buildHref(item, accountUuid)}
                        ref={isAlertItem ? alertsTriggerRef : undefined}
                        $collapsed={collapsed}
                        $contentHidden={contentHidden}
                        className={({ isActive }) => {
                          const active = isActive || isItemActive(item);
                          if (isAlertItem && alertsOpen) return 'is-active';
                          return active ? 'is-active' : '';
                        }}
                        onClick={isAlertItem ? handleAlertsClick : undefined}
                        aria-haspopup={isAlertItem ? 'menu' : undefined}
                        aria-expanded={isAlertItem ? alertsOpen : undefined}
                        aria-label={collapsed ? title : undefined}
                      >
                        {showBadge && collapsed ? (
                          <IconBadgeWrap>
                            {iconNode}
                            <NavBadgeFloat $critical={alertHasCritical} />
                          </IconBadgeWrap>
                        ) : (
                          iconNode
                        )}
                        <NavLabel $collapsed={collapsed}>{title}</NavLabel>
                        {showBadge && !collapsed && <NavBadgeInline $critical={alertHasCritical} />}
                      </NavItemLink>
                    );
                    // Collapsed rail hides text labels; the customer
                    // couldn't identify icons from shape alone. Wrap
                    // in a styled Tooltip (placement=right so it pops
                    // out from the rail, not over content) that only
                    // activates while collapsed — the native `title`
                    // attribute had a ~500 ms browser delay that
                    // made it feel absent.
                    return collapsed ? (
                      <Tooltip
                        key={item.path}
                        title={title}
                        placement="right"
                        enterDelay={100}
                        enterNextDelay={50}
                      >
                        {navItem}
                      </Tooltip>
                    ) : (
                      navItem
                    );
                  })}
                </Subnav>
              )}
            </Group>
          );
        })}
      </Nav>

      <SidebarFooter $collapsed={collapsed}>
        {/* Same quick tooltip treatment as nav items — the native
            `title` attribute had a ~500 ms browser delay, so testers
            reported it felt intermittent. Placement=right so it
            escapes the rail. Works in both collapsed and expanded
            states — the label meaningfully changes ("Expand" vs
            "Collapse"), so it earns a tooltip either way. */}
        <Tooltip
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          placement="right"
          enterDelay={100}
          enterNextDelay={50}
        >
          <FooterToggle
            type="button"
            onClick={handleToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            $contentHidden={contentHidden}
          >
            {collapsed ? <PanelOpenIcon /> : <PanelCloseIcon />}
          </FooterToggle>
        </Tooltip>
        <FooterMeta $collapsed={collapsed} $contentHidden={contentHidden}>
          {process.env.REACT_APP_BUILD_NUMBER && (
            <span>build&nbsp;{process.env.REACT_APP_BUILD_NUMBER}</span>
          )}
          {process.env.REACT_APP_ENV && process.env.REACT_APP_ENV !== 'production' && (
            <EnvPill>{process.env.REACT_APP_ENV}</EnvPill>
          )}
        </FooterMeta>
      </SidebarFooter>

      {alertsOpen && alertsAnchor && (
        <AlertsPanel
          ref={alertsPanelRef}
          role="menu"
          style={{ top: alertsAnchor.top, left: alertsAnchor.left }}
        >
          <AlertsPanelLabel>{titles.alerts || 'Alerts'}</AlertsPanelLabel>
          {(() => {
            const entries = newAlertList ? Object.entries(newAlertList) : [];
            if (entries.length === 0) {
              return <AlertsPanelEmpty>{sidebarT.noNewAlerts || 'No new alerts'}</AlertsPanelEmpty>;
            }
            return entries.map(([key, data]) => {
              const { type, count } = data;
              const isCritical = type === 'critical' && count > 0;
              return (
                <AlertsPanelItem
                  key={key}
                  type="button"
                  role="menuitem"
                  onClick={() => navigateToAlerts([{ id: 'alertTags', value: alertTags?.[key] }])}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {sidebarT[ALERT_GROUP_LABEL_KEYS[key]] || key}
                    </div>
                    {count > 0 && (
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--qhs-text-3)',
                          marginTop: 2,
                        }}
                      >
                        {count}{' '}
                        {count === 1
                          ? sidebarT.alertSingular || 'alert'
                          : sidebarT.alertPlural || 'alerts'}
                      </div>
                    )}
                  </span>
                  {isCritical && <CriticalTag>{sidebarT.critical || 'Critical'}</CriticalTag>}
                </AlertsPanelItem>
              );
            });
          })()}
          <AlertsPanelDivider />
          <AlertsPanelItem type="button" role="menuitem" onClick={() => navigateToAlerts([])}>
            <span style={{ flex: 1, fontWeight: 600, color: 'var(--qhs-brand)' }}>
              {sidebarT.seeAllAlerts || 'See all alerts'}
            </span>
          </AlertsPanelItem>
        </AlertsPanel>
      )}
    </Aside>
  );
};

export default Sidebar;
